// Camera that smooth-follows the player and clamps to the world bounds.
// Exposes a single offset getter; main.ts composes it with the shake
// offset and writes the sum to app.stage.position once per frame.

import type { World } from '../world/types.js';
import type { Stage } from './stage.js';

let camX = 0;
let camY = 0;
let initialized = false;

// Higher = snappier follow. Tuned so the camera feels glued to the
// player at chase speed without overshooting on stop.
const FOLLOW_SMOOTHING = 7;

/** Force the camera onto a position (no lerp). Use after a level change. */
export function snapCamera(x: number, y: number): void {
  camX = x;
  camY = y;
  initialized = true;
}

/** Reset state so the next updateCamera() call snaps. */
export function resetCamera(): void {
  initialized = false;
}

/** Move the camera one frame toward the player and clamp to bounds. */
export function updateCamera(world: World, stage: Stage, dt: number): void {
  const target = world.player;
  if (!initialized) {
    camX = target.x;
    camY = target.y;
    initialized = true;
  } else {
    const k = 1 - Math.exp(-FOLLOW_SMOOTHING * dt);
    camX += (target.x - camX) * k;
    camY += (target.y - camY) * k;
  }

  // Clamp so the camera never reveals area outside the world. If the
  // world is smaller than the viewport on either axis, lock that axis
  // to the world center.
  const halfW = stage.viewportW / 2;
  const halfH = stage.viewportH / 2;
  if (world.WORLD_W <= stage.viewportW) {
    camX = world.WORLD_W / 2;
  } else {
    camX = Math.max(halfW, Math.min(world.WORLD_W - halfW, camX));
  }
  if (world.WORLD_H <= stage.viewportH) {
    camY = world.WORLD_H / 2;
  } else {
    camY = Math.max(halfH, Math.min(world.WORLD_H - halfH, camY));
  }
}

/** World-to-stage translation: where the world (0,0) sits in stage space.
 * Rounded to integer pixels — without this, sub-pixel offsets cause every
 * sprite + texture sample to land between texels, and as the camera
 * lerps smoothly the GPU's linear filter blends a slightly different
 * pair of texels each frame, producing a visible shimmer on edges
 * (especially the wall outlines and sprite silhouettes). */
export function getCameraOffset(stage: Stage): { x: number; y: number } {
  return {
    x: Math.round(stage.viewportW / 2 - camX),
    y: Math.round(stage.viewportH / 2 - camY),
  };
}
